import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Route, Switch, Redirect } from 'react-router-dom';
import { Auth, API } from 'aws-amplify';
import { makeStyles } from '@material-ui/core/styles';
import CssBaseline from '@material-ui/core/CssBaseline';
import Header from './Header';
import Footer from './Footer';

import LoginPage from '../pages/LoginPage';
import HomePage from '../pages/HomePage';
import UsersPage from '../pages/UsersPage';
import UserPage from '../pages/UserPage';
import ProfilePage from '../pages/ProfilePage';
import CustomersPage from '../pages/CustomersPage';
import CustomerPage from '../pages/CustomerPage';
import CustomerUsersPage from '../pages/CustomerUsersPage';
import CustomerUserPage from '../pages/CustomerUserPage';
import ChangeUsersPage from '../pages/ChangeUsersPage';
import TicketsPage from '../pages/TicketsPage';
import TicketPage from '../pages/TicketPage';
import MassEmailNotification from '../pages/MassEmailNotification';
import NotFoundPage from '../pages/NotFoundPage';

import CircularProgress from '@material-ui/core/CircularProgress';
import Typography from '@material-ui/core/Typography';
import { invokeApig } from '../lib/awsLib';
import CustomersInvoicingPage from '../pages/CustomersInvoicingPage';
import config from '../config';

const useStyles = makeStyles((theme) => ({
    root: {
      display: 'flex',
    },
    appBarSpacer: theme.mixins.toolbar,
    content: {
        flexGrow: 1,
        height: '100vh',
        overflow: 'auto',
        '& > div:nth-of-type(2)': {
            minHeight: 'calc(100% - 198px)',
        }
    },
    loadingWrapper: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        backgroundColor: theme.palette.background.default,
    },
    loadingBox: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: theme.spacing(3),
        padding: theme.spacing(5),
        borderRadius: theme.shape.borderRadius * 2,
        backgroundColor: theme.palette.background.paper,
        boxShadow: theme.shadows[3],
        minWidth: 300,
    },
    loadingTitle: {
        fontWeight: 700,
        color: '#00b31f',
        letterSpacing: 1,
    },
    loadingStatus: {
        color: theme.palette.text.secondary,
        fontSize: '0.9rem',
    },
    loadingVersion: {
        color: theme.palette.text.disabled,
        fontSize: '0.75rem',
        marginTop: theme.spacing(1),
    },
}));

const PrivateRoute = ({ component: Component, ...rest }) => {
    return (
        <Route
            {...rest}
            render={props =>
                rest.isAuthenticated ? (
                    <Component {...props} params={rest}/>
                ) : (
                    <Redirect to="/login" />
                )
            }
        />
    );
};

const Layout = () => {
    const classes = useStyles();
    const [isLoading, setIsLoading] = useState(true);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [currentUser, setCurrentUser] = useState({});
    const [access, setAccess] = useState([]);
    const [company, setCompany] = useState('');

    useEffect(() => {
        checkAuthState();
    }, []);

    async function getAccess() {
        try {
            const response = await invokeApig({
                path: '/user/profile'
            });
            if (response.code === 0) {
                setAccess(response.data.user_access);
                setCompany(response.data.company);
            }
        } catch (error) {
            console.error('Error in getAccess:', error);
        }
    }

    const checkAuthState = async () => {
        try {
            const user = await Auth.currentAuthenticatedUser({ bypassCache: true });
            setCurrentUser(user);
            setIsAuthenticated(true);
            await getAccess();
            setIsLoading(false);
        } catch (err) {
            setIsAuthenticated(false);
            setIsLoading(false);
        }
    };

    const handleUpdateProfile = () => {
        checkAuthState();
    }

    return (
        isLoading ?
        <div className={classes.loadingWrapper}>
            <div className={classes.loadingBox}>
                <Typography variant="h5" className={classes.loadingTitle}>
                    Manager Neokode
                </Typography>
                <CircularProgress size={48} />
                <Typography className={classes.loadingStatus}>
                    Validando acceso...
                </Typography>
                <Typography className={classes.loadingVersion}>
                    v{config.version}
                </Typography>
            </div>
        </div>
        :
        (
            isAuthenticated ?
            <Router>
                <div className={classes.root}>
                    <CssBaseline />
                    <Header user={currentUser} isAuthenticated={isAuthenticated} access={access} company={company} />
                    <main className={classes.content}>
                        <div className={classes.appBarSpacer} />
                        <Switch>
                            <PrivateRoute exact path="/" component={HomePage} isAuthenticated={isAuthenticated} access={access} />
                            <PrivateRoute exact path="/users" component={UsersPage} isAuthenticated={isAuthenticated} access={access} />
                            <PrivateRoute exact path="/users/create" component={UserPage} isAuthenticated={isAuthenticated} access={access} />
                            <PrivateRoute exact path="/users/edit/:id" component={UserPage} isAuthenticated={isAuthenticated} access={access} currentUser={currentUser} />
                            <PrivateRoute exact path="/profile" component={ProfilePage} isAuthenticated={isAuthenticated} access={access} onUpdate={handleUpdateProfile} />
                            <PrivateRoute exact path="/customers" component={CustomersPage} isAuthenticated={isAuthenticated} access={access} />
                            <PrivateRoute exact path="/customers/:id/users" component={CustomerUsersPage} isAuthenticated={isAuthenticated} access={access} />
                            <PrivateRoute exact path="/customers/:id/users/create" component={CustomerUserPage} isAuthenticated={isAuthenticated} access={access} />
                            <PrivateRoute exact path="/customers/:id/users/edit/:idu" component={CustomerUserPage} isAuthenticated={isAuthenticated} access={access} />
                            <PrivateRoute exact path="/customers/create" component={CustomerPage} isAuthenticated={isAuthenticated} access={access} />
                            <PrivateRoute exact path="/customers/edit/:id" component={CustomerPage} isAuthenticated={isAuthenticated} access={access} />
                            <PrivateRoute exact path="/customers/users" component={ChangeUsersPage} isAuthenticated={isAuthenticated} access={access} />
                            <PrivateRoute exact path="/billing/process" component={CustomersInvoicingPage} isAuthenticated={isAuthenticated} access={access} />
                            <PrivateRoute exact path="/tickets" component={TicketsPage} isAuthenticated={isAuthenticated} access={access} />
                            <PrivateRoute exact path="/tickets/create" component={TicketPage} isAuthenticated={isAuthenticated} access={access} />
                            <PrivateRoute exact path="/tickets/edit/:id" component={TicketPage} isAuthenticated={isAuthenticated} access={access} />
                            <PrivateRoute exact path="/notification/new" component={MassEmailNotification} isAuthenticated={isAuthenticated} access={access} />
                            <Route path="/login" component={LoginPage} />
                            <Route component={NotFoundPage} />
                        </Switch>
                        <Footer />
                    </main>
                </div>
            </Router>
            :
            <Router>
                <Switch>
                    <Route path="/" component={LoginPage} />
                    <Route path="/login" component={LoginPage} />
                    <Route component={NotFoundPage} />
                </Switch>
            </Router>
        )
    );
};

export default Layout;
